import type { Melody, MelodyGrid, MelodyMeter, MelodyNote, Meter } from '@tunasong/schemas'
import { isDottedEasyScoreDuration, undotted } from '../easy-score/duration.js'
import type { EasyScoreDuration } from '../easy-score/duration.js'
import { isTripletGrid, melodyToSuggestion, TRIPLET_RATIO } from './convert.js'
import { barBeats, GRID_TOLERANCE } from './grid.js'

/**
 * Flatten a melody into an EasyScore `lang` string for an ADDITIVE staff sibling (§3).
 *
 * The sibling is a VIEW: the melody stays the source of truth, and the flattening is lossy on
 * purpose. Every loss is reported rather than swallowed, so the command can tell the user what
 * the staff will not show:
 *
 *   - `offsets-dropped` - notation has no way to draw "12 ms late".
 *   - `duration-rounded` - an onset or length off the sixteenth grid, snapped to it; or any note on
 *     a triplet grid, which has no bracket here.
 *   - `rest-dropped`    - on a triplet grid only: a gap between notes is not drawn there.
 *
 * GH #1985: on a straight grid every line is exactly one bar of the melody's meter. VexFlow formats
 * a voice strictly against the time signature, so one short bar (a gap, a pickup, a partial last
 * bar) or one long bar (a note held over the barline) blanked the whole staff. Gaps are written as
 * rests, the last bar is filled with rests, and a note that crosses a barline - or has a length no
 * single value can draw, like 2.5 beats - is split into values tied with `~`. Notes and rests are
 * split by the beat, not longest value first (`beatTokens`), so the beat stays readable.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §3
 */

export interface MelodyRenderLoss {
  kind: 'offsets-dropped' | 'duration-rounded' | 'rest-dropped' | 'out-of-range'
  noteIndex?: number
  message: string
}

/**
 * The values a staff can draw a note as, in quarter-note beats. SHORTEST first, so `reduce`'s
 * strict `<` keeps the earlier entry on an exact tie and the tie resolves SHORT - the rule
 * `convert.ts` applies: a lengthened note overruns the next onset, a shortened one never does.
 *
 * Durations are read from the melody's exact beats, NOT through the AI suggestion enum
 * (`melodyDurations`): that enum is the vocabulary a model may answer in, and it has no dots. A
 * melody captured or edited by hand holds a dotted quarter as 1.5 beats; squeezed through the enum
 * it came out a quarter and left the bar short.
 */
const NOTATED_VALUES: { beats: number; token: EasyScoreDuration; dotted: boolean }[] = [
  { beats: 0.25, token: '16', dotted: false },
  { beats: 0.5, token: '8', dotted: false },
  { beats: 0.75, token: '8.', dotted: true },
  { beats: 1, token: 'q', dotted: false },
  { beats: 1.5, token: 'q.', dotted: true },
  { beats: 2, token: 'h', dotted: false },
  { beats: 3, token: 'h.', dotted: true },
  { beats: 4, token: 'w', dotted: false },
]

/** The smallest value a straight-grid staff is written in. All the bar arithmetic is in these. */
const SIXTEENTHS_PER_BEAT = 4

interface Value {
  length: number
  token: EasyScoreDuration
}

/** The same values in sixteenths, LONGEST first, for splitting a length into tied values. */
const VALUES_IN_SIXTEENTHS: Value[] = NOTATED_VALUES.map(v => ({
  length: v.beats * SIXTEENTHS_PER_BEAT,
  token: v.token,
})).reverse()

const UNDOTTED_IN_SIXTEENTHS: Value[] = VALUES_IN_SIXTEENTHS.filter(v => !isDottedEasyScoreDuration(v.token))

/** The pitch a rest is written at. VexFlow needs one; on a treble stave B4 puts it mid-stave. */
const REST_PITCH = 'B4'

/** A rest event. VexFlow's grammar puts a rest's dot after the type: `B4/h/r.`, not `B4/h./r`. */
const restEvent = (token: EasyScoreDuration): string =>
  isDottedEasyScoreDuration(token) ? `${REST_PITCH}/${undotted(token)}/r.` : `${REST_PITCH}/${token}/r`

/**
 * The value a note of `beats` is drawn as. On a triplet grid the length is first scaled up by 3/2
 * into the value a reader sees under the bracket, and only undotted values are candidates: a dot
 * inside a tuplet is rare, and a full beat (1.5 notated) drawn as a dotted quarter would silently
 * read the triplet grid as straight.
 */
const notatedValue = (beats: number, grid: MelodyGrid): { token: EasyScoreDuration; beats: number } => {
  const triplet = isTripletGrid(grid)
  const notated = triplet ? beats * TRIPLET_RATIO : beats
  const candidates = triplet ? NOTATED_VALUES.filter(v => !v.dotted) : NOTATED_VALUES
  const best = candidates.reduce((carry, entry) =>
    Math.abs(entry.beats - notated) < Math.abs(carry.beats - notated) ? entry : carry
  )
  return { token: best.token, beats: best.beats }
}

/** The value exactly `length` long, if there is one. */
const exactValue = (values: Value[], length: number): Value | undefined => values.find(v => v.length === length)

/** Split a length into values, longest first: 10 sixteenths is a half and an eighth. */
const splitLength = (length: number, values: Value[] = VALUES_IN_SIXTEENTHS): EasyScoreDuration[] => {
  const value = values.find(v => v.length <= length)
  return length > 0 && value ? [value.token, ...splitLength(length - value.length, values)] : []
}

/**
 * Split from `start` to `end` so each value starts on a multiple of its own length - a sixteenth
 * at an odd sixteenth, then an eighth - smallest first. The rest of a beat is written this way, so
 * every value sits where it is counted. Undotted values only.
 */
const alignedSplit = (start: number, end: number): EasyScoreDuration[] => {
  const value = UNDOTTED_IN_SIXTEENTHS.find(v => start % v.length === 0 && start + v.length <= end)
  return start < end && value ? [value.token, ...alignedSplit(start + value.length, end)] : []
}

const repeat = (times: number, value: number) => Array.from({ length: times }, () => value)

/** Eighths per beat group in x/8: threes when the top divides by three, else twos ending in a three. */
const eighthGroups = (top: number): number[] => {
  if (top % 3 === 0) {
    return repeat(top / 3, 3)
  }
  if (top < 3) {
    return [top]
  }
  return top % 2 === 0 ? repeat(top / 2, 2) : [...repeat((top - 3) / 2, 2), 3]
}

/**
 * How a bar of this meter is counted, in sixteenths from the barline.
 *
 * - `beats`: where each beat starts, and the bar's end. A quarter in x/4; three eighths in compound
 *   meters (6/8, 9/8, 12/8); in other x/8 meters groups of two eighths, the last one three when
 *   the top is odd - 7/8 counts 2+2+3.
 * - `splits`: points no value may cross - the middle of the bar in simple meters with an even
 *   number of beats (beat 3 of 4/4), so the bar's two halves stay visible.
 * - `restValues`: undotted only in x/4; a dotted quarter is a whole beat, or group, in x/8.
 * - `mergeRestBeats`: in x/4 one rest may cover several beats (a half rest on beats 1-2); in x/8
 *   each beat, or group, gets its own rest.
 */
interface BarCount {
  beats: number[]
  splits: number[]
  restValues: Value[]
  mergeRestBeats: boolean
  /**
   * x/8: a single note value from a beat must end on a beat boundary, or it hides the next beat
   * inside a group (four eighths from beat 1 of 6/8 are `q.~8`, not `h`). It may span whole groups
   * (`h.` in 12/8). In x/4 a dotted quarter or half from a beat is standard and stays one value.
   */
  singleEndsOnBeat: boolean
}

/** May one value run from a beat to `end` without hiding a beat? */
const singleMayEndAt = (count: BarCount, end: number) => !count.singleEndsOnBeat || count.beats.includes(end)

const barCount = (meter: MelodyMeter): BarCount => {
  const { top, bottom } = meter
  if (bottom === 4) {
    const beats = Array.from({ length: top + 1 }, (_, beat) => beat * SIXTEENTHS_PER_BEAT)
    const half = top % 2 === 0 && top >= 4 ? [(top / 2) * SIXTEENTHS_PER_BEAT] : []
    return { beats, splits: half, restValues: UNDOTTED_IN_SIXTEENTHS, mergeRestBeats: true, singleEndsOnBeat: false }
  }
  const beats = eighthGroups(top).reduce((starts, eighths) => [...starts, starts[starts.length - 1] + eighths * 2], [0])
  return { beats, splits: [], restValues: VALUES_IN_SIXTEENTHS, mergeRestBeats: false, singleEndsOnBeat: true }
}

/**
 * The values from `start` to `end` within one bar, split by the beat (Bowie M4):
 *
 * 1. a value that starts off the beat first fills up to the next beat - a note longest first, a
 *    rest aligned (`alignedSplit`), so in compound meters a quarter rest only ever covers the first
 *    two eighths of a beat;
 * 2. then whole beats - a note that starts on a beat may take any single value that fits exactly
 *    (a dotted quarter on beat 1 of 4/4), otherwise the longest run of whole beats one value can
 *    write;
 * 3. what is left of a last, partial beat is split as in 1.
 *
 * The caller has already cut the span at the barlines and at `splits`.
 */
const beatTokens = (start: number, end: number, count: BarCount, rest: boolean): EasyScoreDuration[] => {
  const values = rest ? count.restValues : VALUES_IN_SIXTEENTHS
  const fill = (from: number, to: number) => (rest ? alignedSplit(from, to) : splitLength(to - from, values))
  const tokens: EasyScoreDuration[] = []
  let at = start
  if (!count.beats.includes(at)) {
    const nextBeat = Math.min(end, count.beats.find(b => b > start) ?? end)
    tokens.push(...fill(at, nextBeat))
    at = nextBeat
  }
  while (at < end) {
    const from = at
    const single = rest || !singleMayEndAt(count, end) ? undefined : exactValue(values, end - from)
    const reachable = count.beats.filter(b => b > from && b <= end)
    const candidates = rest && !count.mergeRestBeats ? reachable.slice(0, 1) : reachable
    const run = single
      ? { to: end, value: single }
      : candidates
          .map(to => ({ to, value: exactValue(values, to - from) }))
          .reverse()
          .find(c => c.value !== undefined)
    if (!run?.value) {
      tokens.push(...fill(from, end))
      break
    }
    tokens.push(run.value.token)
    at = run.to
  }
  return tokens
}

/**
 * The values for one piece of a note or rest within a bar. Read from the downbeat, nothing is
 * hidden by one value: a note from beat 1 is one value whenever one fits (a whole note, or a dotted
 * half in 4/4), and so is a rest that fills the whole bar (a whole rest in 4/4). Otherwise the piece
 * is cut at the bar's `splits` and each part split by the beat.
 */
const pieceTokens = (start: number, end: number, count: BarCount, rest: boolean): EasyScoreDuration[] => {
  const barLength = count.beats[count.beats.length - 1]
  const wholeBarRest = rest && start === 0 && end === barLength ? exactValue(count.restValues, end) : undefined
  const downbeatNote =
    !rest && start === 0 && singleMayEndAt(count, end) ? exactValue(VALUES_IN_SIXTEENTHS, end) : undefined
  const single = wholeBarRest ?? downbeatNote
  if (single) {
    return [single.token]
  }
  const cuts = [start, ...count.splits.filter(s => s > start && s < end), end]
  return cuts.slice(1).flatMap((to, index) => beatTokens(cuts[index], to, count, rest))
}

/** A stretch of the timeline in sixteenths: a note (by its index in the melody) or a rest. */
interface Span {
  start: number
  end: number
  noteIndex?: number
}

/**
 * The melody laid on the sixteenth grid as notes and the rests between them, from the start of bar
 * 1 to the end of the bar the last note ends in. A staff is one voice, so a note that overlaps the
 * next is cut off where the next starts.
 */
const timeline = (melody: Melody, barLength: number, losses: MelodyRenderLoss[]): Span[] => {
  const spans: Span[] = []
  const onsets = melody.notes.map(n => Math.round(n.onset * SIXTEENTHS_PER_BEAT))
  let cursor = 0
  melody.notes.forEach((source, index) => {
    const start = Math.max(onsets[index], cursor)
    const next = onsets[index + 1]
    const snappedEnd = Math.max(start + 1, Math.round((source.onset + source.duration) * SIXTEENTHS_PER_BEAT))
    const end = next !== undefined && next > start ? Math.min(snappedEnd, next) : snappedEnd
    if (
      Math.abs(start / SIXTEENTHS_PER_BEAT - source.onset) > GRID_TOLERANCE ||
      Math.abs((end - start) / SIXTEENTHS_PER_BEAT - source.duration) > GRID_TOLERANCE
    ) {
      losses.push({
        kind: 'duration-rounded',
        noteIndex: index,
        message: `Note ${index + 1}: drawn on the nearest sixteenth.`,
      })
    }
    if (start > cursor) {
      spans.push({ start: cursor, end: start })
    }
    spans.push({ start, end, noteIndex: index })
    cursor = end
  })
  const lastBarEnd = Math.ceil(cursor / barLength) * barLength
  if (lastBarEnd > cursor) {
    spans.push({ start: cursor, end: lastBarEnd })
  }
  return spans
}

/** Bars of EasyScore events for a straight grid, each adding up to exactly `barLength`. */
const straightBars = (melody: Melody, suggestion: MelodyNote[], losses: MelodyRenderLoss[]): string[][] => {
  const { meter } = melody.tempo
  const barLength = barBeats(meter) * SIXTEENTHS_PER_BEAT
  const count = barCount(meter)
  const bars: string[][] = []
  const push = (bar: number, events: string[]) => {
    bars[bar] = [...(bars[bar] ?? []), ...events]
  }

  for (const span of timeline(melody, barLength, losses)) {
    /** Cut at every barline the span crosses. */
    const pieces: { bar: number; start: number; end: number }[] = []
    for (let at = span.start; at < span.end;) {
      const bar = Math.floor(at / barLength)
      const end = Math.min(span.end, (bar + 1) * barLength)
      pieces.push({ bar, start: at - bar * barLength, end: end - bar * barLength })
      at = end
    }

    if (span.noteIndex === undefined) {
      pieces.forEach(p => push(p.bar, pieceTokens(p.start, p.end, count, true).map(restEvent)))
      continue
    }

    const { pitch, octave } = suggestion[span.noteIndex]
    const perPiece = pieces.map(p => pieceTokens(p.start, p.end, count, false))
    const total = perPiece.reduce((sum, tokens) => sum + tokens.length, 0)
    let written = 0
    pieces.forEach((p, index) => {
      push(
        p.bar,
        perPiece[index].map(token => {
          written += 1
          /** Every value but the note's last is tied to the next, across a barline too. */
          return `${pitch}${octave}/${token}${written < total ? '~' : ''}`
        })
      )
    })
  }
  return bars
}

/**
 * Triplet grids: there are no tuplet brackets in the score language, so bars of triplets cannot
 * add up against the time signature. Each note is written as its nearest notated value, rests are
 * not drawn, and every rounding is reported.
 */
const tripletBars = (melody: Melody, suggestion: MelodyNote[], losses: MelodyRenderLoss[]): string[][] => {
  const values = melody.notes.map(note => notatedValue(note.duration, melody.grid))
  melody.notes.forEach((source, index) => {
    if (Math.abs(values[index].beats - source.duration) > GRID_TOLERANCE) {
      losses.push({
        kind: 'duration-rounded',
        noteIndex: index,
        message: `Note ${index + 1}: ${source.duration} beats drawn as ${values[index].token}.`,
      })
    }
    const previous = melody.notes[index - 1]
    if (previous && source.onset - (previous.onset + previous.duration) > GRID_TOLERANCE) {
      losses.push({
        kind: 'rest-dropped',
        noteIndex: index,
        message: `Note ${index + 1}: the rest before it is not drawn.`,
      })
    }
  })

  const perBar = barBeats(melody.tempo.meter)
  const bars: string[][] = []
  suggestion.forEach((s, index) => {
    const bar = Math.floor(melody.notes[index].onset / perBar)
    bars[bar] = [...(bars[bar] ?? []), `${s.pitch}${s.octave}/${values[index].token}`]
  })
  return bars.filter(Boolean)
}

export const melodyToScoreLang = (melody: Melody): { lang: string; losses: MelodyRenderLoss[] } => {
  const losses: MelodyRenderLoss[] = []
  if (melody.notes.length === 0) {
    return { lang: '', losses }
  }

  if (melody.notes.some(n => Math.round(Math.abs(n.offsetMs)) > 0)) {
    losses.push({
      kind: 'offsets-dropped',
      message: 'The staff cannot show the timing offsets - the melody keeps them.',
    })
  }

  const suggestion = melodyToSuggestion(melody)
  const bars = isTripletGrid(melody.grid)
    ? tripletBars(melody, suggestion, losses)
    : straightBars(melody, suggestion, losses)

  return {
    lang: bars.map(bar => bar.join(', ')).join('\n'),
    losses,
  }
}

/** The tops a score's meter can hold - `meterSchema`'s `notesPerBar` union. */
const SCORE_METER_TOPS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const

function isScoreMeterTop(top: number): top is Meter['notesPerBar'] {
  return (SCORE_METER_TOPS as readonly number[]).includes(top)
}

/**
 * GH #1984: the meter a staff sibling stores, so the block engraves in the meter `melodyToScoreLang`
 * cut the bars in, whatever the song's meter. `notesPerBar` is the TOP number and `beatsPerBar` the
 * BOTTOM one - the persisted names are inverted. Undefined when a score meter cannot hold the
 * melody's (a top of 1, or above 12); "Show as staff" then refuses rather than emit a staff barred
 * in one meter and engraved in another.
 */
export const scoreMeterOf = (melody: Melody): Meter | undefined => {
  const { top, bottom } = melody.tempo.meter
  return isScoreMeterTop(top) ? { notesPerBar: top, beatsPerBar: bottom } : undefined
}
